import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "../common/Select";
import Input from "./../common/Input";
import Checkbox from "../common/Checkbox";
import ReportsExpand from "../common/ReportsExpand";
import {
  getFormsyReportContactsList,
  getFormsyReportSubmissionsList,
  getProgramUnits,
} from "../services/soar";
import { getFormList } from "./../forms/forms";

function FormsyReport({
  reportType,
  title,
  isFavorite,
  toggleFavorite,
  ...rest
}) {
  const [reportData, setReportData] = useState({
    Company: "",
    ProgramUnit: "",
    BeginDate: "",
    EndDate: "",
  });

  const [programOptions, setProgramOptions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [monthlyReports, setMonthlyReports] = useState([]);
  const isMountedRef = useRef(true);

  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  const companyOptions = useMemo(() => {
    function onlyUnique(value, index, self) {
      return self.indexOf(value) === index;
    }

    const companies = safyInfo.user.safyInfo.divisions
      .filter((division) => division.Active === "1")
      .map((division) => division.CompanyName)
      .filter(onlyUnique)
      .sort();

    return companies.map((company) => {
      return { _id: company, name: company };
    });
  }, [safyInfo.user.safyInfo.divisions]);

  const handleChange = ({ currentTarget: input }) => {
    const value = input.type === "checkbox" ? input.checked : input.value;

    console.log("input: ", input, value);
    const data = { ...reportData };
    data[input.name] = value;
    setReportData(data);
  };

  const handleCompanyChange = async (e) => {
    handleChange(e);
    const input = e.currentTarget;

    if (!input.value) {
      return;
    }

    setLoading(true);
    const programUnits = await getProgramUnits(input.value);

    if (!isMountedRef.current) {
      return;
    }

    if (!programUnits) {
      setLoading(false);
      return;
    }

    const unitsWithReportFormNames = programUnits.filter((unit) => {
      if (!unit.sor_safyportalconfig) {
        return false;
      }

      try {
        const portalConfig = JSON.parse(unit.sor_safyportalconfig);

        if (
          !portalConfig?.reportFormNames?.length === 0 ||
          !portalConfig.reportFormNames[reportType]
        ) {
          return false;
        }
      } catch {
        console.log("BrokenProgramUnitPortalConfig:", unit.ccx_name);
        return false;
      }

      return true;
    });

    const options = unitsWithReportFormNames.map((programUnit) => {
      const portalConfig = JSON.parse(programUnit.sor_safyportalconfig);

      return {
        _id: programUnit.ccx_name,
        name: programUnit.ccx_name,
        reportFormName: portalConfig.reportFormNames[reportType],
      };
    });

    setProgramOptions(options);
    setLoading(false);
  };

  const loadContacts = async () => {
    setContacts([]);
    setMonthlyReports([]);
    const selectedUnit = programOptions.filter(
      (option) => option._id === reportData.ProgramUnit
    )[0];
    const formInfo = getFormList(selectedUnit.reportFormName);

    const results = await getFormsyReportContactsList(
      formInfo.formId,
      reportData.ProgramUnit,
      formInfo.reportFieldNames,
      [reportData.BeginDate, reportData.EndDate]
    );

    if (!isMountedRef.current) {
      return;
    }

    if (results.length === 0) {
      toast.info("No reports found");
    }

    setContacts(
      results.sort((a, b) => a.ContactName.localeCompare(b.ContactName))
    );
  };

  const handleContactSelected = (clientId) => {
    if (selectedContacts.includes(clientId)) {
      const remainingSelectedContacts = selectedContacts.filter(
        (contactId) => contactId !== clientId
      );
      setSelectedContacts(remainingSelectedContacts);
    } else {
      const obj = [...selectedContacts];
      obj.push(clientId);
      setSelectedContacts(obj);
    }
  };

  const getMonthlyReports = async () => {
    const selectedUnit = programOptions.filter(
      (option) => option._id === reportData.ProgramUnit
    )[0];
    const formInfo = getFormList(selectedUnit.reportFormName);

    const response = await getFormsyReportSubmissionsList(
      formInfo.formId,
      reportData.ProgramUnit,
      formInfo.reportFieldNames,
      [reportData.BeginDate, reportData.EndDate],
      selectedContacts
    );

    setMonthlyReports(
      response.sort((a, b) => a.ContactName.localeCompare(b.ContactName))
    );
  };

  const getReportLink = (submission) => {
    const selectedUnit = programOptions.filter(
      (option) => option._id === reportData.ProgramUnit
    )[0];

    const formConfig = getFormList(selectedUnit.reportFormName);

    if (formConfig) {
      return `/${formConfig.formPath}/${submission.FormVersionId}/${submission.ContactId}/${submission.SubmissionId}`;
    }
  };

  return (
    <ReportsExpand
      title={title}
      isFavorite={isFavorite}
      toggleFavorite={toggleFavorite}
      {...rest}
    >
      <div className="d-flex flex-column">
        <div className="d-flex flex-column flex-md-row align-items-start mb-3">
          <div className="me-md-3 mb-3 mb-md-0" style={{ minWidth: "200px" }}>
            <label className="form-label">Company</label>
            <Select
              name="Company"
              options={companyOptions}
              onChange={handleCompanyChange}
              hideLabel={true}
            />
          </div>

          {!loading ? (
            <>
              <div
                className="me-md-3 mb-3 mb-md-0"
                style={{ minWidth: "200px" }}
              >
                <label className="form-label">Program Unit</label>
                <Select
                  name="ProgramUnit"
                  options={programOptions}
                  onChange={handleChange}
                  hideLabel={true}
                />
              </div>

              <div
                className="me-md-3 mb-3 mb-md-0"
                style={{ minWidth: "200px" }}
              >
                <label className="form-label">Begin Date</label>
                <Input
                  name="BeginDate"
                  type="date"
                  onChange={handleChange}
                  hideLabel={true}
                />
              </div>

              <div
                className="me-md-3 mb-3 mb-md-0"
                style={{ minWidth: "200px" }}
              >
                <label className="form-label">End Date</label>
                <Input
                  name="EndDate"
                  type="date"
                  onChange={handleChange}
                  hideLabel={true}
                />
              </div>

              <div className="d-flex align-items-end mb-md-0">
                {reportData.ProgramUnit !== "" &&
                  reportData.BeginDate !== "" &&
                  reportData.EndDate !== "" && (
                    <i
                      className="fas fa-arrow-circle-right fa-3x"
                      onClick={loadContacts}
                    ></i>
                  )}
              </div>
            </>
          ) : (
            <>Loading...</>
          )}
        </div>

        {contacts.length > 0 && monthlyReports.length === 0 ? (
          <>
            <div className="row">
              <div className="col">
                <Checkbox
                  name="SelectAll"
                  label="Select All"
                  checked={selectedContacts.length === contacts.length}
                  onChange={() => {
                    selectedContacts.length === contacts.length
                      ? setSelectedContacts([])
                      : setSelectedContacts(
                          contacts.map((contact) => contact.ContactId)
                        );
                  }}
                  divClassName="ms-3"
                />
              </div>
            </div>
            <div className="row mt-3 g-3">
              <div className="col-md-11">
                <div className="container rounded bg-white py-2">
                  {contacts.map((contact) => {
                    return (
                      <div key={contact.ContactId} className="row">
                        <div className="col">
                          <Checkbox
                            name={`contact.${contact.ContactId}`}
                            label={contact.ContactName}
                            checked={selectedContacts.includes(
                              contact.ContactId
                            )}
                            onChange={() => {
                              handleContactSelected(contact.ContactId);
                            }}
                            divClassName="ms-3"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="col-md-1 d-flex align-items-center justify-content-start">
                <i
                  className="fas fa-arrow-circle-right fa-3x"
                  onClick={getMonthlyReports}
                ></i>
              </div>
            </div>
          </>
        ) : null}

        {monthlyReports.length > 0 ? (
          <div className="row mt-3">
            <div className="col">
              <div className="container rounded bg-white py-2">
                {monthlyReports.map((report) => {
                  return (
                    <div
                      key={report.SubmissionId}
                      className="row border-bottom"
                    >
                      <div className="col-10 col-md-11">
                        <Link
                          to={getReportLink(report)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {report.ContactName}
                        </Link>
                      </div>
                      <div className="col-2 col-md-1 text-center">
                        <Link
                          to={getReportLink(report)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="fas fa-folder-open fa-lg"></i>
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </ReportsExpand>
  );
}

export default FormsyReport;
